import type { ReactNode } from "react";
import { Lock } from "lucide-react";
import clsx from "clsx";

/**
 * Marks a field as filled by the system rather than by the person.
 *
 * A disabled input on its own reads as broken — as though it failed to load,
 * or the form is misbehaving. The padlock says the value is deliberate and
 * comes from somewhere else, so nobody hunts for a way to type in it.
 *
 * Wrap only the control, never the label or a validation message: the icon is
 * centred against its container, so including a message that appears and
 * disappears would make the icon drift as the field grows.
 *
 * The wrapped control needs right padding of its own — pass its class through
 * {@link lockedInputClass}, which adds that padding and the muted fill.
 */
export function LockedField({
  children,
  locked = true,
  className,
}: {
  children: ReactNode;
  /** Same field, editable in some contexts: hides the padlock when false. */
  locked?: boolean;
  className?: string;
}) {
  return (
    <div className={clsx("relative", className)}>
      {children}
      {locked && (
        <Lock
          size={14}
          aria-hidden="true"
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[rgb(var(--text-muted))]"
        />
      )}
    </div>
  );
}

/**
 * Adds the read-only treatment to an input's own classes: room on the right
 * for the padlock, the muted fill, and a cursor that doesn't promise typing.
 */
export function lockedInputClass(base: string, locked = true) {
  return locked
    ? clsx(base, "pr-9 !bg-[rgb(var(--card-muted))] cursor-not-allowed")
    : base;
}
